/**
 * ChatMessage - Individual chat message bubble
 */

import { cn } from '@/utils/cn';
import type { ChatMessage as ChatMessageType } from '@/types';

interface ChatMessageProps {
  message: ChatMessageType;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.type === 'user';
  const isAssistant = message.type === 'assistant';
  const time = formatTime(message.timestamp);

  // Determine sender label
  const senderLabel = isUser ? 'YOU:' : isAssistant ? 'BLURP.IE:' : 'SYSTEM:';

  return (
    <div
      className={cn(
        'flex flex-col max-w-[85%]',
        isUser ? 'ml-auto items-end' : 'mr-auto items-start'
      )}
    >
      {/* Sender label */}
      <div
        className={cn(
          'text-[10px] font-hud uppercase tracking-wider mb-1 px-1',
          isUser ? 'text-hud-secondary' : 'text-hud-primary'
        )}
      >
        {senderLabel}
      </div>

      {/* Message bubble */}
      <div
        className={cn(
          'px-3 py-2 rounded-lg',
          'text-sm leading-relaxed',
          isUser
            ? 'bg-hud-secondary/20 border border-hud-secondary/30 text-text-primary rounded-br-none'
            : isAssistant
            ? 'bg-hud-primary/15 border border-hud-primary/30 text-text-primary rounded-bl-none'
            : 'bg-hud-primary/10 border border-hud-primary/20 text-text-primary rounded-bl-none'
        )}
      >
        {message.content}
      </div>

      {/* Timestamp */}
      <div className="text-[10px] text-text-muted mt-1 px-1">
        {time}
      </div>
    </div>
  );
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default ChatMessage;
